import { useReplaceBrandFontFile } from '../api/mutations';
import { fontContentHash } from './content-hash';
import { fontFamilyOf, readFontMetadata } from './font-metadata';
import type { AssetUploader } from './useFontUpload';

export interface FontReplace {
  /** Resolves once the font holds the new file; rejects with what went wrong. */
  replace: (fontId: string, file: File) => Promise<void>;
  isReplacing: boolean;
}

/**
 * Swaps the file behind a font, the same two steps an upload takes: the bytes to assets-api, then
 * the row to guidelines-api. The file is read on the way past for what it says about itself, as an
 * upload reads it, so the panel describes the new file rather than the one it replaced.
 */
export function useFontReplace(uploadAsset: AssetUploader): FontReplace {
  const replaceFile = useReplaceBrandFontFile();

  const replace = async (fontId: string, file: File): Promise<void> => {
    const metadata = await readFontMetadata(file);
    const contentHash = await fontContentHash(await file.arrayBuffer());
    const asset = await uploadAsset.mutateAsync(file);
    await replaceFile.mutateAsync({
      fontId,
      assetId: asset.id,
      filename: file.name,
      ...metadata,
      contentHash,
      // Filed under the same family rule an upload uses, so a replaced Condensed stays a Condensed.
      ...(fontFamilyOf(metadata) ? { familyName: fontFamilyOf(metadata) } : {}),
    });
  };

  return { replace, isReplacing: uploadAsset.isPending || replaceFile.isPending };
}
